import type { TourScene } from './tour-types';

export const OPD_SCENES: readonly TourScene[] = [
  {
    id: 'opd-login',
    chapter: 'o-start',
    route: '/login',
    title: 'Sign in',
    label: 'Sign in',
    caption: {
      heading: 'One sign-in page for every facility',
      body: 'Type your organisation, the short name in your web address, then your username and password. The page shows your hospital name as soon as it knows who you are. Hospitals, laboratories and medical stores all sign in here.',
      tip: 'Give every staff member their own login. The audit log records who did what, and a shared account hides that.',
    },
  },
  {
    id: 'opd-start-dashboard',
    chapter: 'o-start',
    route: '/dashboard',
    title: 'Dashboard',
    label: 'Dashboard',
    caption: {
      heading: 'Your day at a glance',
      body: 'The dashboard shows what is happening now: consultations started, new patients, bed occupancy, money collected, admissions, discharges and the lab and imaging work still pending.',
      tip: 'The cards and the left menu follow your plan and your role. A receptionist never sees Accounting, so nobody hunts through screens they cannot use.',
    },
  },
  {
    id: 'opd-admin-team',
    chapter: 'o-start',
    route: '/people',
    title: 'People',
    label: 'People',
    caption: {
      heading: 'Who works here and what they may do',
      body: 'Every person is on one list, whether they sign in, are on the payroll, or both. Add a person, give them a login and a role, and the menus and actions follow. The Roles tab lists the ready-made roles for doctors, nurses, cashiers, pharmacists and more.',
      tip: 'Two-factor authentication can be required for administrative and financial roles in Settings.',
    },
  },
  {
    id: 'opd-admin-settings',
    chapter: 'o-start',
    route: '/admin/settings',
    title: 'Settings',
    label: 'Settings',
    caption: {
      heading: 'Make it yours',
      body: 'Set the display name and colour, the currency, the follow-up window, the session timeout and the ward stock model. Numbering, laboratory, integrations and update information have their own tabs.',
      tip: 'Licensed modules are listed at the bottom. Your provider switches them on, and the menu follows.',
    },
  },
  {
    id: 'opd-patients-search',
    chapter: 'o-patients',
    route: '/patients',
    title: 'Patients',
    label: 'Search',
    caption: {
      heading: 'Find a patient in a few keystrokes',
      body: 'Search by MRN, name, phone number or CNIC. The list narrows as you type, and each row shows the patient category and the date they were registered.',
      tip: 'Always search before you register. There is one lifetime record per person, so a returning patient keeps their whole history.',
    },
  },
  {
    id: 'opd-patients-register',
    chapter: 'o-patients',
    route: '/patients/new',
    title: 'Register a patient',
    label: 'Register',
    caption: {
      heading: 'Check first, then register once',
      body: 'Search the CNIC or mobile number before anything else, so the same person is never entered twice. If nobody comes back, fill in the identity and contact details, give an approximate age when the date of birth is unknown, and press Register.',
      tip: 'One mobile number usually covers a whole family, so pick the right person by name from what comes back.',
    },
  },
  {
    id: 'opd-patients-chart',
    chapter: 'o-patients',
    route: '/patients/01a0c481…',
    title: 'Patient chart',
    label: 'Chart',
    caption: {
      heading: 'The chart opens with a new MRN',
      body: 'Details, guardians and contacts sit on the left. The clinical timeline, previous visits, billing and documents are tabs on the right. Allergies and guardians are added from the same screen, and Book appointment is one click away.',
      tip: 'Record allergies before the first prescription. They are checked every time a doctor issues one.',
    },
  },
  {
    id: 'opd-clinic-hours',
    chapter: 'o-clinic',
    route: '/schedules',
    title: 'Clinic hours',
    label: 'Clinic hours',
    caption: {
      heading: 'Publish clinic hours before anyone can book',
      body: 'Until a doctor has a weekly template, the booking screen has no slots to offer. Choose the doctor, the branch, the day, the start and end time and the slot length, then publish.',
      tip: 'Publishing again replaces the doctor’s current template. Appointments already booked keep their times.',
    },
  },
  {
    id: 'opd-clinic-book',
    chapter: 'o-clinic',
    route: '/appointments',
    title: 'Book an appointment',
    label: 'Book',
    caption: {
      heading: 'Pick the patient, the doctor and a free slot',
      body: 'Find the patient, choose the doctor and the date, and the free slots appear. Pick one, note the reason for the visit and book.',
      tip: 'Slots come straight from the clinic hours, so nobody can book a time the doctor does not work. A full slot needs an explicit overbook seat.',
    },
  },
  {
    id: 'opd-clinic-arrive',
    chapter: 'o-clinic',
    route: '/appointments',
    title: 'Mark as arrived',
    label: 'Arrived',
    caption: {
      heading: 'Mark the patient as arrived',
      body: 'When the patient reaches the desk, press Arrived. They join the doctor’s queue in the order they came, with a token number. No-show and Cancel sit beside it.',
      tip: 'The status column always tells the truth about the day, so nobody waits for a patient who is not coming.',
    },
  },
  {
    id: 'opd-clinic-vitals',
    chapter: 'o-clinic',
    route: '/queue',
    title: 'Queue and vitals',
    label: 'Vitals',
    caption: {
      heading: 'The queue and the vitals',
      body: 'The queue lists who is waiting, for how long, and whether vitals are recorded or a fee is due. Record blood pressure, pulse, temperature, oxygen saturation and weight, then press Start to open the consultation.',
      tip: 'Anyone with the right role can take vitals, so a nurse can prepare the patient before the doctor calls them in.',
    },
  },
  {
    id: 'opd-clinic-note',
    chapter: 'o-clinic',
    route: '/consultations/01a0c481…',
    title: 'Clinical note',
    label: 'Note',
    caption: {
      heading: 'The clinical note saves itself',
      body: 'Complaints, history, examination and plan are written in one place. The note autosaves as you type, so a closed browser or a dropped connection loses nothing.',
      tip: 'Previous visits are one tab away, so the doctor can read what happened last time without leaving the screen.',
    },
  },
  {
    id: 'opd-clinic-diagnosis',
    chapter: 'o-clinic',
    route: '/consultations/01a0c481…',
    title: 'Diagnosis',
    label: 'Diagnosis',
    caption: {
      heading: 'Search the ICD-10 list by name or code',
      body: 'Type a word and the matching diagnoses appear; misspellings are tolerated. Add one, mark it primary, set it as provisional or confirmed and save.',
      tip: 'A consultation cannot be closed until at least one diagnosis is recorded.',
    },
  },
  {
    id: 'opd-clinic-prescription',
    chapter: 'o-clinic',
    route: '/consultations/01a0c481…',
    title: 'Prescription',
    label: 'Prescription',
    caption: {
      heading: 'Write the prescription in seconds',
      body: 'Add each medicine with its strength, dose, frequency and number of days, add advice for the patient, then Issue and print. The prescription goes straight to the pharmacy queue.',
      tip: 'Allergy and interaction warnings appear as you write. A severe one has to be acknowledged before the prescription can be issued.',
    },
  },
  {
    id: 'opd-clinic-orders',
    chapter: 'o-clinic',
    route: '/consultations/01a0c481…',
    title: 'Order tests',
    label: 'Orders',
    caption: {
      heading: 'Order tests from the same screen',
      body: 'Choose laboratory or imaging, set the priority, then search for a test or a panel and click to add it. Order, and the lab sees it at once. The bill for those tests is raised automatically.',
      tip: 'Nothing is retyped anywhere. The technician sees the order the moment the doctor places it.',
    },
  },
  {
    id: 'opd-clinic-close',
    chapter: 'o-clinic',
    route: '/consultations/01a0c481…',
    title: 'Close the consultation',
    label: 'Close',
    caption: {
      heading: 'Close the consultation',
      body: 'Closing marks the appointment completed and locks the note. The tests stay visible on the visit with their status, from ordered through to released.',
      tip: 'The Full record button opens the patient’s whole history if the doctor needs more context.',
    },
  },
  {
    id: 'opd-billing-shift',
    chapter: 'o-billing',
    route: '/billing/shift',
    title: 'Cashier shift',
    label: 'Open a shift',
    caption: {
      heading: 'Open a shift before taking payments',
      body: 'A cashier opens a shift with the opening float, the cash already in the drawer. Everything collected from then on is counted against that shift.',
      tip: 'At the end of the day the cashier counts the drawer note by note and closes the shift. The system compares the count with what it expected.',
    },
  },
  {
    id: 'opd-billing-invoices',
    chapter: 'o-billing',
    route: '/billing',
    title: 'Invoices',
    label: 'Invoices',
    caption: {
      heading: 'Invoices raise themselves',
      body: 'The tests the doctor ordered in the consultation already have their invoice, with its context, payer, status, total and balance. Nobody typed an invoice.',
      tip: 'Filter by status or search by invoice number, patient name or MRN when a patient comes back with a question.',
    },
  },
  {
    id: 'opd-billing-collect',
    chapter: 'o-billing',
    route: '/billing/invoices/01a0c481…',
    title: 'Collect payment',
    label: 'Collect',
    caption: {
      heading: 'Collect the payment',
      body: 'Open the invoice to see every charge, then press Collect payment. Choose cash, card, bank transfer, mobile wallet, online gateway or credit panel. For cash, enter what was tendered and the change is worked out for you.',
      tip: 'The receipt and an A4 bill print from the same screen. Partial payments are allowed and the balance stays on the invoice.',
    },
  },
  {
    id: 'opd-billing-drawer',
    chapter: 'o-billing',
    route: '/billing/shift',
    title: 'Shift totals',
    label: 'Drawer',
    caption: {
      heading: 'The drawer balances itself',
      body: 'The shift page updates as payments arrive: what was collected, how many invoices, the cash expected in the drawer and the total by payment mode.',
      tip: 'Counting the drawer at close by note denomination makes a shortage visible on the same day it happens.',
    },
  },
  {
    id: 'opd-admin-reports',
    chapter: 'o-reports',
    route: '/reports',
    title: 'Reports',
    label: 'Reports',
    caption: {
      heading: 'Reports for any date range',
      body: 'Daily census, revenue, collections, receivables, doctors, diagnoses and KPIs, each for the dates you choose. The Builder tab lets you compose a report of your own.',
      tip: 'Collections and receivables are built from the invoices and payments your cashiers record.',
    },
  },
  {
    id: 'opd-admin-audit',
    chapter: 'o-reports',
    route: '/admin/audit',
    title: 'Audit log',
    label: 'Audit log',
    caption: {
      heading: 'Every change is on record',
      body: 'Every create, update and delete on a clinical or financial record is listed with the time, the person, the table and the address they were signed in from. Filter by table or by action.',
      tip: 'The Hash chain intact badge means no entry has been altered or removed since it was written.',
    },
  },
];
